import { Injectable, inject } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import {
  Category,
  EmploymentType,
  OfferEducation,
  OfferExperience,
  Priority,
  SalaryPeriod,
} from '@core/offers-types';

@Injectable({ providedIn: 'root' })
export class FixedSelectOptionsTranslations {
  private readonly i18n = inject(I18nService);

  // EMPLOYMENT TYPES
  public readonly employmentTypes: { [key in EmploymentType]: string } = {
    [EmploymentType.FullTime]: this.i18n.translate(
      'app.options.offer.employment_types_v2.fulltime',
    ),
    [EmploymentType.FullTimePermanent]: `${this.i18n.translate(
      'app.options.offer.employment_types_v2.fulltime',
    )}, ${this.i18n.translate('app.options.offer.employment_types_v2.nested_options.permanent')}`,
    [EmploymentType.FullTimeFixedTerm]: `${this.i18n.translate(
      'app.options.offer.employment_types_v2.fulltime',
    )}, ${this.i18n.translate('app.options.offer.employment_types_v2.nested_options.fixed_term')}`,
    [EmploymentType.PartTime]: this.i18n.translate(
      'app.options.offer.employment_types_v2.parttime',
    ),
    [EmploymentType.PartTimePermanent]: `${this.i18n.translate(
      'app.options.offer.employment_types_v2.parttime',
    )}, ${this.i18n.translate('app.options.offer.employment_types_v2.nested_options.permanent')}`,
    [EmploymentType.PartTimeFixedTerm]: `${this.i18n.translate(
      'app.options.offer.employment_types_v2.parttime',
    )}, ${this.i18n.translate('app.options.offer.employment_types_v2.nested_options.fixed_term')}`,
    [EmploymentType.PartTimeMinijob]: `${this.i18n.translate(
      'app.options.offer.employment_types_v2.parttime',
    )}, ${this.i18n.translate('app.options.offer.employment_types_v2.nested_options.minijob')}`,
    [EmploymentType.Freelance]: this.i18n.translate(
      'app.options.offer.employment_types_v2.freelance',
    ),
    [EmploymentType.Contract]: this.i18n.translate(
      'app.options.offer.employment_types_v2.contract',
    ),
    [EmploymentType.Temporary]: this.i18n.translate(
      'app.options.offer.employment_types_v2.temporary',
    ),
    [EmploymentType.Internship]: this.i18n.translate(
      'app.options.offer.employment_types_v2.internship',
    ),
    [EmploymentType.Apprenticeship]: this.i18n.translate(
      'app.options.offer.employment_types_v2.apprenticeship',
    ),
    [EmploymentType.Seasonal]: this.i18n.translate(
      'app.options.offer.employment_types_v2.seasonal',
    ),
    [EmploymentType.Volunteer]: this.i18n.translate(
      'app.options.offer.employment_types_v2.volunteer',
    ),
  };

  // EDUCATIONS
  public readonly educations: { [key in OfferEducation]: string } = {
    [OfferEducation.HighSchoolCoursework]: this.i18n.translate(
      'app.options.offer.educations.high_school_coursework',
    ),
    [OfferEducation.HighSchool]: this.i18n.translate('app.options.offer.educations.high_school'),
    [OfferEducation.Certification]: this.i18n.translate(
      'app.options.offer.educations.certification',
    ),
    [OfferEducation.Vocational]: this.i18n.translate('app.options.offer.educations.vocational'),
    [OfferEducation.CollegeCoursework]: this.i18n.translate(
      'app.options.offer.educations.college_coursework',
    ),
    [OfferEducation.AssociateDegree]: this.i18n.translate(
      'app.options.offer.educations.associate_degree',
    ),
    [OfferEducation.BachelorDegree]: this.i18n.translate(
      'app.options.offer.educations.bachelor_degree',
    ),
    [OfferEducation.MasterDegree]: this.i18n.translate(
      'app.options.offer.educations.master_degree',
    ),
    [OfferEducation.Doctorate]: this.i18n.translate('app.options.offer.educations.doctorate'),
    [OfferEducation.Professional]: this.i18n.translate('app.options.offer.educations.professional'),
  };

  // EXPERIENCES
  public readonly experiences: { [key in OfferExperience]: string } = {
    [OfferExperience.StudentSchool]: this.i18n.translate(
      'app.options.offer.experiences.student_school',
    ),
    [OfferExperience.StudentCollege]: this.i18n.translate(
      'app.options.offer.experiences.student_college',
    ),
    [OfferExperience.EntryLevel]: this.i18n.translate('app.options.offer.experiences.entry_level'),
    [OfferExperience.MidLevel]: this.i18n.translate('app.options.offer.experiences.mid_level'),
    [OfferExperience.Experienced]: this.i18n.translate('app.options.offer.experiences.experienced'),
    [OfferExperience.Manager]: this.i18n.translate('app.options.offer.experiences.manager'),
    [OfferExperience.SeniorManager]: this.i18n.translate(
      'app.options.offer.experiences.senior_manager',
    ),
    [OfferExperience.Executive]: this.i18n.translate('app.options.offer.experiences.executive'),
    [OfferExperience.SeniorExecutive]: this.i18n.translate(
      'app.options.offer.experiences.senior_executive',
    ),
  };

  // CATEGORIES
  public readonly categories: { [key in Category]: string } = {
    [Category.Accountancy]: this.i18n.translate('app.options.offer.categories.accountancy'),
    [Category.Administrative]: this.i18n.translate('app.options.offer.categories.administrative'),
    [Category.Advertising]: this.i18n.translate('app.options.offer.categories.advertising'),
    [Category.Agriculture]: this.i18n.translate('app.options.offer.categories.agriculture'),
    [Category.ArchitecturalServices]: this.i18n.translate(
      'app.options.offer.categories.architectural_services',
    ),
    [Category.ArtsEntertainment]: this.i18n.translate(
      'app.options.offer.categories.arts_entertainment',
    ),
    [Category.Automotive]: this.i18n.translate('app.options.offer.categories.automotive'),
    [Category.Banking]: this.i18n.translate('app.options.offer.categories.banking'),
    [Category.BiotechPharma]: this.i18n.translate('app.options.offer.categories.biotech_pharma'),
    [Category.Cleaning]: this.i18n.translate('app.options.offer.categories.cleaning'),
    [Category.Construction]: this.i18n.translate('app.options.offer.categories.construction'),
    [Category.Consulting]: this.i18n.translate('app.options.offer.categories.consulting'),
    [Category.CustomerService]: this.i18n.translate(
      'app.options.offer.categories.customer_service',
    ),
    [Category.Design]: this.i18n.translate('app.options.offer.categories.design'),
    [Category.Education]: this.i18n.translate('app.options.offer.categories.education'),
    [Category.Energy]: this.i18n.translate('app.options.offer.categories.energy'),
    [Category.Engineering]: this.i18n.translate('app.options.offer.categories.engineering'),
    [Category.Finance]: this.i18n.translate('app.options.offer.categories.finance'),
    [Category.GovernmentNonprofit]: this.i18n.translate(
      'app.options.offer.categories.government_nonprofit',
    ),
    [Category.Healthcare]: this.i18n.translate('app.options.offer.categories.healthcare'),
    [Category.Hospitality]: this.i18n.translate('app.options.offer.categories.hospitality'),
    [Category.InformationTechnology]: this.i18n.translate(
      'app.options.offer.categories.information_technology',
    ),
    [Category.Insurance]: this.i18n.translate('app.options.offer.categories.insurance'),
    [Category.Internet]: this.i18n.translate('app.options.offer.categories.internet'),
    [Category.LegalServices]: this.i18n.translate('app.options.offer.categories.legal_services'),
    [Category.Leisure]: this.i18n.translate('app.options.offer.categories.leisure'),
    [Category.Logistics]: this.i18n.translate('app.options.offer.categories.logistics'),
    [Category.Management]: this.i18n.translate('app.options.offer.categories.management'),
    [Category.Manufacturing]: this.i18n.translate('app.options.offer.categories.manufacturing'),
    [Category.MarketingPR]: this.i18n.translate('app.options.offer.categories.marketing_pr'),
    [Category.Procurement]: this.i18n.translate('app.options.offer.categories.procurement'),
    [Category.Property]: this.i18n.translate('app.options.offer.categories.property'),
    [Category.Publishing]: this.i18n.translate('app.options.offer.categories.publishing'),
    [Category.RecruitmentHr]: this.i18n.translate('app.options.offer.categories.recruitment_hr'),
    [Category.Retail]: this.i18n.translate('app.options.offer.categories.retail'),
    [Category.Sales]: this.i18n.translate('app.options.offer.categories.sales'),
    [Category.Security]: this.i18n.translate('app.options.offer.categories.security'),
    [Category.Science]: this.i18n.translate('app.options.offer.categories.science'),
    [Category.Technical]: this.i18n.translate('app.options.offer.categories.technical'),
    [Category.Telecommunication]: this.i18n.translate(
      'app.options.offer.categories.telecommunication',
    ),
    [Category.Tourism]: this.i18n.translate('app.options.offer.categories.tourism'),
    [Category.TranslationServices]: this.i18n.translate(
      'app.options.offer.categories.translation_services',
    ),
    [Category.Other]: this.i18n.translate('app.options.offer.categories.other'),
  };

  // SALARY PERIODS
  public readonly salaryPeriods: { [key in SalaryPeriod]: string } = {
    [SalaryPeriod.Hour]: this.i18n.translate('app.options.offer.salary_periods.hour'),
    [SalaryPeriod.Month]: this.i18n.translate('app.options.offer.salary_periods.month'),
    [SalaryPeriod.Year]: this.i18n.translate('app.options.offer.salary_periods.year'),
  };

  // Priority
  public readonly priorities: { [key in Priority]: string } = {
    [Priority.Low]: this.i18n.translate('app.options.offer.priorities.low'),
    [Priority.Normal]: this.i18n.translate('app.options.offer.priorities.normal'),
    [Priority.High]: this.i18n.translate('app.options.offer.priorities.high'),
    [Priority.Urgent]: this.i18n.translate('app.options.offer.priorities.urgent'),
  };
}
